import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { X, Settings, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "ascauto_cookie_consent";

function getConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function saveConsent(prefs) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...prefs, ts: Date.now() }));
}

export function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [prefs, setPrefs] = useState({ necessary: true, preferences: false, analytics: false });
  const [texts, setTexts] = useState(null);

  useEffect(() => {
    const consent = getConsent();
    if (!consent) setVisible(true);
    axios.get(`${API}/api/config/privacy`).then(r => setTexts(r.data)).catch(() => {});
  }, []);

  const accept = () => { saveConsent({ necessary: true, preferences: true, analytics: true }); setVisible(false); };
  const reject = () => { saveConsent({ necessary: true, preferences: false, analytics: false }); setVisible(false); };
  const saveConfig = () => { saveConsent(prefs); setVisible(false); setShowConfig(false); };

  if (!visible) return null;

  const title = texts?.cookie_banner_title || "Uso de cookies";
  const text = texts?.cookie_banner_text || "Utilizamos cookies propias y de terceros para mejorar tu experiencia de navegacion, analizar el uso del sitio y personalizar contenido. Puedes aceptar todas las cookies, rechazarlas o configurar tus preferencias.";
  const btnAccept = texts?.cookie_banner_accept || "Aceptar";
  const btnReject = texts?.cookie_banner_reject || "Rechazar";
  const btnConfigure = texts?.cookie_banner_configure || "Configurar";

  return (
    <div className="fixed bottom-0 inset-x-0 z-[60] p-2 sm:p-6" data-testid="cookie-banner">
      <div className="max-w-4xl mx-auto bg-slate-900 border border-slate-700 rounded-sm shadow-2xl overflow-hidden">
        {/* Main banner */}
        {!showConfig && (
          <div className="p-3 sm:p-6">
            <div className="flex items-start gap-2 sm:gap-3">
              <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h3 className="text-xs sm:text-sm font-bold text-white uppercase tracking-wider mb-1">{title}</h3>
                <p className="text-[11px] sm:text-xs text-slate-400 leading-snug mb-2 sm:mb-4 line-clamp-3 sm:line-clamp-none">{text}</p>
                <p className="hidden sm:block text-[10px] text-slate-500 mb-4">
                  Mas informacion en nuestra{" "}
                  <Link to="/politica-de-cookies" className="text-red-400 underline hover:text-red-300">Politica de cookies</Link>{" "}y{" "}
                  <Link to="/politica-de-privacidad" className="text-red-400 underline hover:text-red-300">Politica de privacidad</Link>.
                </p>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  <Button
                    onClick={accept}
                    className="bg-red-600 hover:bg-red-700 text-white rounded-sm h-8 sm:h-9 px-3 sm:px-5 text-[11px] sm:text-xs uppercase tracking-wider font-bold flex-1 sm:flex-none"
                    data-testid="cookie-accept-btn"
                  >
                    {btnAccept}
                  </Button>
                  <Button
                    onClick={reject}
                    variant="outline"
                    className="border-slate-600 text-slate-300 hover:text-white hover:border-slate-400 rounded-sm h-8 sm:h-9 px-3 sm:px-5 text-[11px] sm:text-xs uppercase tracking-wider font-semibold bg-transparent flex-1 sm:flex-none"
                    data-testid="cookie-reject-btn"
                  >
                    {btnReject}
                  </Button>
                  <Button
                    onClick={() => setShowConfig(true)}
                    variant="ghost"
                    className="text-slate-400 hover:text-white rounded-sm h-8 sm:h-9 px-2 sm:px-3 text-[11px] sm:text-xs uppercase tracking-wider font-semibold"
                    data-testid="cookie-configure-btn"
                  >
                    <Settings className="w-3.5 h-3.5 mr-1" />{" "}<span className="hidden sm:inline">{btnConfigure}</span>
                  </Button>
                </div>
              </div>
              <button onClick={reject} className="text-slate-400 hover:text-white transition-colors p-1 flex-shrink-0" data-testid="cookie-close-btn" aria-label="Cerrar">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Configuration panel */}
        {showConfig && (
          <div className="p-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-white uppercase tracking-wider">Configurar cookies</h3>
              <button onClick={() => setShowConfig(false)} className="text-slate-500 hover:text-white transition-colors p-1">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-3 mb-5">
              <CookieOption
                id="necessary"
                label="Cookies necesarias"
                desc="Imprescindibles para el funcionamiento basico del sitio web. No se pueden desactivar."
                checked={true}
                disabled
              />
              <CookieOption
                id="preferences"
                label="Cookies de preferencias"
                desc="Permiten recordar tus opciones de idioma, centro y configuracion."
                checked={prefs.preferences}
                onChange={v => setPrefs(p => ({...p, preferences: v}))}
              />
              <CookieOption
                id="analytics"
                label="Cookies analiticas"
                desc="Nos ayudan a entender como se usa el sitio web con datos anonimos y agregados."
                checked={prefs.analytics}
                onChange={v => setPrefs(p => ({...p, analytics: v}))}
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={saveConfig}
                className="bg-red-600 hover:bg-red-700 text-white rounded-sm h-9 px-5 text-xs uppercase tracking-wider font-bold"
                data-testid="cookie-save-config-btn"
              >
                Guardar preferencias
              </Button>
              <Button
                onClick={accept}
                variant="outline"
                className="border-slate-600 text-slate-300 hover:text-white hover:border-slate-400 rounded-sm h-9 px-5 text-xs uppercase tracking-wider font-semibold bg-transparent"
              >
                Aceptar todas
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CookieOption({ id, label, desc, checked, onChange, disabled }) {
  return (
    <div className="flex items-start gap-3 bg-slate-800/50 rounded-sm p-3">
      <Checkbox
        id={`cookie-${id}`}
        checked={checked}
        onCheckedChange={onChange}
        disabled={disabled}
        className="mt-0.5 border-slate-500"
        data-testid={`cookie-opt-${id}`}
      />
      <label htmlFor={`cookie-${id}`} className={`${disabled ? "" : "cursor-pointer"}`}>
        <span className="text-xs font-semibold text-white block">{label}</span>
        <span className="text-[11px] text-slate-400 leading-relaxed">{desc}</span>
      </label>
    </div>
  );
}

/**
 * Reopener link for footer or settings page
 */
export function CookieSettingsLink({ className = "" }) {
  const reopen = () => {
    localStorage.removeItem(STORAGE_KEY);
    window.location.reload();
  };
  return (
    <button onClick={reopen} className={`text-xs hover:text-white transition-colors ${className}`} data-testid="cookie-settings-reopen">
      Configurar cookies
    </button>
  );
}
